import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  HandHeart, Users, Heart, Briefcase, GraduationCap, Scale, Stethoscope, Sparkles,
  ArrowRight, ShieldCheck, Palette, Calendar, Laptop,
} from "lucide-react";

const AREAS = [
  { icon: GraduationCap, title: "Educação", body: "Apoio escolar, tutoria e adaptações pedagógicas para nossas crianças." },
  { icon: Scale, title: "Direito", body: "Orientação jurídica em direitos previdenciários, escolares e da saúde." },
  { icon: Stethoscope, title: "Saúde e Terapias", body: "Consultas, terapias e acompanhamento com médicos, TO, fono e psicólogos." },
  { icon: Palette, title: "Comunicação", body: "Design, redação e social media para levar a mensagem do instituto mais longe." },
  { icon: Calendar, title: "Eventos", body: "Produção de encontros presenciais, campanhas e ações comunitárias." },
  { icon: Laptop, title: "Tecnologia", body: "Manutenção da plataforma, automações e novos recursos digitais." },
];

const NUMBERS = [
  { n: "+3.000", label: "famílias em nossa rede" },
  { n: "26", label: "estados atendidos" },
  { n: "24/7", label: "acolhimento pelo WhatsApp" },
];

export default function Support() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const gotoAction = () => {
    if (!user) navigate("/cadastro");
    else navigate("/perfil");
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[hsl(var(--secondary))]/10 via-white to-[hsl(var(--primary))]/10" aria-hidden />
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[hsl(var(--secondary))]/20 blur-3xl -z-10" aria-hidden />
        <div className="container-cuidar py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-widest uppercase text-[hsl(var(--secondary))] bg-white/60 border border-[hsl(var(--secondary))]/25 rounded-full px-3 py-1">
              <Sparkles className="h-3.5 w-3.5" /> Área do Apoiador
            </div>
            <h1 className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05]">
              Cuide de quem cuida.<br />
              <span className="text-[hsl(var(--primary))]">Seja apoiador(a).</span>
            </h1>
            <p className="mt-6 text-lg text-[hsl(var(--muted-foreground))] max-w-2xl">
              Você não precisa ser mãe, pai ou responsável de uma criança neuroatípica para
              fazer parte do Instituto Cuidar. Voluntários e doadores, pessoa física ou empresa —
              todos são bem-vindos para transformar realidades juntos.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={gotoAction}
                className="btn-pill h-12 px-7 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                data-testid="support-cta-join-btn"
              >
                <HandHeart className="mr-2 h-5 w-5" /> {user ? "Ativar meu perfil de apoiador" : "Quero apoiar"}
              </Button>
              <Button
                onClick={() => navigate("/doacoes")}
                variant="outline"
                className="btn-pill h-12 px-7"
                data-testid="support-cta-donate-btn"
              >
                <Heart className="mr-2 h-5 w-5 text-[hsl(var(--secondary))]" /> Fazer uma doação
              </Button>
            </div>

            <div className="mt-12 flex flex-wrap gap-8">
              {NUMBERS.map((n) => (
                <div key={n.label}>
                  <div className="font-heading text-3xl md:text-4xl font-black text-[hsl(var(--primary))]">{n.n}</div>
                  <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-1">{n.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Two paths */}
      <section className="container-cuidar py-14">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="surface p-8 border-2 border-[hsl(var(--primary))]/25">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--primary))]/12 grid place-items-center text-[hsl(var(--primary))]">
              <Users className="h-7 w-7" />
            </div>
            <h2 className="mt-5 font-heading text-2xl font-black">Voluntário(a)</h2>
            <p className="mt-3 text-[hsl(var(--muted-foreground))]">
              Você doa tempo e conhecimento. Escolha as áreas em que quer contribuir — nós te
              conectamos com projetos e famílias que precisam do seu talento.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /> Você define disponibilidade e áreas</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /> Recebe convites para projetos alinhados ao seu perfil</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--primary))] mt-0.5" /> Certificado de horas voluntárias ao final de cada ciclo</li>
            </ul>
          </div>

          <div className="surface p-8 border-2 border-[hsl(var(--secondary))]/25 bg-[hsl(var(--secondary))]/5">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--secondary))]/15 grid place-items-center text-[hsl(var(--secondary))]">
              <Heart className="h-7 w-7" />
            </div>
            <h2 className="mt-5 font-heading text-2xl font-black">Doador(a)</h2>
            <p className="mt-3 text-[hsl(var(--muted-foreground))]">
              Sua contribuição financeira sustenta terapias, materiais, plataformas e a equipe
              que orienta famílias diariamente. Cada real conta.
            </p>
            <ul className="mt-5 space-y-2 text-sm">
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--secondary))] mt-0.5" /> Pessoa física ou empresa</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--secondary))] mt-0.5" /> Doação única ou recorrente (mensal automática — em breve via Stripe)</li>
              <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[hsl(var(--secondary))] mt-0.5" /> Relatórios mensais de impacto no seu e-mail</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Areas of contribution */}
      <section className="container-cuidar pb-16">
        <div className="text-xs font-bold uppercase tracking-widest text-[hsl(var(--secondary))]">Onde você pode ajudar</div>
        <h2 className="mt-2 font-heading text-3xl md:text-4xl font-black">Áreas de contribuição</h2>
        <p className="mt-3 text-[hsl(var(--muted-foreground))] max-w-2xl">
          Escolha as áreas que combinam com sua história e experiência. Todo apoio soma.
        </p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {AREAS.map((a, i) => (
            <div key={a.title} className="surface p-6 hover:-translate-y-1 hover:shadow-lg transition-all" style={{ animation: `fadeIn 0.4s ${i * 40}ms both` }}>
              <div className="h-11 w-11 rounded-xl bg-[hsl(var(--muted))] grid place-items-center text-[hsl(var(--primary))]">
                <a.icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-heading font-extrabold text-lg">{a.title}</div>
              <p className="mt-1.5 text-sm text-[hsl(var(--muted-foreground))]">{a.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="container-cuidar pb-24">
        <div className="rounded-3xl bg-[hsl(var(--primary))] text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-white/70">Pronto para começar?</div>
            <h3 className="mt-3 font-heading text-2xl md:text-3xl font-black">
              Sua ajuda transforma o dia a dia de crianças reais.
            </h3>
            <p className="mt-3 text-white/85 max-w-xl">
              Ative seu perfil de apoiador em menos de 2 minutos e escolha como quer contribuir.
              Você poderá alterar tudo depois no seu perfil.
            </p>
          </div>
          <Button
            onClick={gotoAction}
            className="btn-pill h-12 px-7 bg-white text-[hsl(var(--primary))] hover:bg-white/95 shrink-0"
            data-testid="support-final-cta-btn"
          >
            Ativar apoio <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Business partnership hint */}
      <section className="container-cuidar pb-24">
        <div className="surface p-8 flex items-start gap-5">
          <div className="h-14 w-14 rounded-2xl bg-[hsl(var(--primary))]/12 grid place-items-center text-[hsl(var(--primary))] shrink-0">
            <Briefcase className="h-6 w-6" />
          </div>
          <div>
            <div className="font-heading font-black text-xl">Sua empresa quer apoiar?</div>
            <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
              Cadastre-se como pessoa jurídica e nossa equipe entrará em contato para desenhar
              um plano de parceria — patrocínio de eventos, campanhas específicas, matching de
              doações de funcionários, e mais.
            </p>
            <Button onClick={() => navigate(user ? "/perfil" : "/cadastro")} variant="outline" className="btn-pill mt-4" data-testid="support-company-cta">
              Cadastrar empresa
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
